import type { ItemModifiers } from '../types/item';

/**
 * Definición de un encantamiento
 */
export interface EnchantmentDefinition {
  id: string;
  name: string;
  description?: string;
  modifiers: ItemModifiers;
  /**
   * Prefijo opcional que decora el nombre del item al formatearlo
   * (ej. "Mágico Sword"). Usado por `formatItemName` en `utils/items.ts`.
   * Si no se define, no se aplica decoración de prefijo.
   */
  prefix?: string;
  /**
   * Sufijo opcional que decora el nombre del item al formatearlo
   * (ej. "Sword de Poder"). Si no se define, no se aplica.
   */
  suffix?: string;
}

/**
 * Base de datos de encantamientos disponibles en el juego
 * Cada encantamiento tiene un ID único y sus modificadores
 */
export const ENCHANTMENTS: Record<string, EnchantmentDefinition> = {
  // Encantamientos de Vida
  enchant_hp_001: {
    id: 'enchant_hp_001',
    name: 'Encantamiento de Vida Menor',
    description: '+20 HP máximo',
    modifiers: { hp: 20 },
  },
  enchant_hp_002: {
    id: 'enchant_hp_002',
    name: 'Encantamiento de Vida',
    description: '+50 HP máximo',
    modifiers: { hp: 50 },
  },
  enchant_hp_003: {
    id: 'enchant_hp_003',
    name: 'Encantamiento de Vida Mayor',
    description: '+100 HP máximo',
    modifiers: { hp: 100 },
  },
  enchant_hp_004: {
    id: 'enchant_hp_004',
    name: 'Encantamiento Maestro de Vida',
    description: '+200 HP máximo',
    modifiers: { hp: 200 },
  },

  // Encantamientos de Mana
  enchant_mana_001: {
    id: 'enchant_mana_001',
    name: 'Encantamiento de Mana Menor',
    description: '+30 Mana máximo',
    modifiers: { mana: 30 },
  },
  enchant_mana_002: {
    id: 'enchant_mana_002',
    name: 'Encantamiento de Mana',
    description: '+70 Mana máximo',
    modifiers: { mana: 70 },
  },
  enchant_mana_003: {
    id: 'enchant_mana_003',
    name: 'Encantamiento de Mana Mayor',
    description: '+150 Mana máximo',
    modifiers: { mana: 150 },
  },
  enchant_mana_004: {
    id: 'enchant_mana_004',
    name: 'Encantamiento Maestro de Mana',
    description: '+300 Mana máximo',
    modifiers: { mana: 300 },
  },

  // Encantamientos de Daño
  enchant_damage_001: {
    id: 'enchant_damage_001',
    name: 'Encantamiento de Daño Menor',
    description: '+2-5 Daño',
    modifiers: { minDamage: 2, maxDamage: 5 },
  },
  enchant_damage_002: {
    id: 'enchant_damage_002',
    name: 'Encantamiento de Daño',
    description: '+5-10 Daño',
    modifiers: { minDamage: 5, maxDamage: 10 },
  },
  enchant_damage_003: {
    id: 'enchant_damage_003',
    name: 'Encantamiento de Daño Mayor',
    description: '+10-20 Daño',
    modifiers: { minDamage: 10, maxDamage: 20 },
  },
  enchant_damage_004: {
    id: 'enchant_damage_004',
    name: 'Encantamiento Maestro de Daño',
    description: '+20-40 Daño',
    modifiers: { minDamage: 20, maxDamage: 40 },
  },

  // Encantamientos de Defensa
  enchant_def_phys_001: {
    id: 'enchant_def_phys_001',
    name: 'Encantamiento de Defensa Física Menor',
    description: '+5 Defensa Física',
    modifiers: { physDef: 5 },
  },
  enchant_def_phys_002: {
    id: 'enchant_def_phys_002',
    name: 'Encantamiento de Defensa Física',
    description: '+10 Defensa Física',
    modifiers: { physDef: 10 },
  },
  enchant_def_phys_003: {
    id: 'enchant_def_phys_003',
    name: 'Encantamiento de Defensa Física Mayor',
    description: '+20 Defensa Física',
    modifiers: { physDef: 20 },
  },
  enchant_def_phys_004: {
    id: 'enchant_def_phys_004',
    name: 'Encantamiento Maestro de Defensa Física',
    description: '+40 Defensa Física',
    modifiers: { physDef: 40 },
  },
  enchant_def_magic_001: {
    id: 'enchant_def_magic_001',
    name: 'Encantamiento de Defensa Mágica Menor',
    description: '+5 Defensa Mágica',
    modifiers: { magicDef: 5 },
  },
  enchant_def_magic_002: {
    id: 'enchant_def_magic_002',
    name: 'Encantamiento de Defensa Mágica',
    description: '+10 Defensa Mágica',
    modifiers: { magicDef: 10 },
  },
  enchant_def_magic_003: {
    id: 'enchant_def_magic_003',
    name: 'Encantamiento de Defensa Mágica Mayor',
    description: '+20 Defensa Mágica',
    modifiers: { magicDef: 20 },
  },
  enchant_def_magic_004: {
    id: 'enchant_def_magic_004',
    name: 'Encantamiento Maestro de Defensa Mágica',
    description: '+40 Defensa Mágica',
    modifiers: { magicDef: 40 },
  },

  // Encantamientos de Velocidad
  enchant_speed_move_001: {
    id: 'enchant_speed_move_001',
    name: 'Encantamiento de Velocidad de Movimiento Menor',
    description: '+0.1 Velocidad de Movimiento',
    modifiers: { moveSpeed: 0.1 },
  },
  enchant_speed_move_002: {
    id: 'enchant_speed_move_002',
    name: 'Encantamiento de Velocidad de Movimiento',
    description: '+0.2 Velocidad de Movimiento',
    modifiers: { moveSpeed: 0.2 },
  },
  enchant_speed_move_003: {
    id: 'enchant_speed_move_003',
    name: 'Encantamiento de Velocidad de Movimiento Mayor',
    description: '+0.3 Velocidad de Movimiento',
    modifiers: { moveSpeed: 0.3 },
  },
  enchant_speed_move_004: {
    id: 'enchant_speed_move_004',
    name: 'Encantamiento Maestro de Velocidad de Movimiento',
    description: '+0.5 Velocidad de Movimiento',
    modifiers: { moveSpeed: 0.5 },
  },
  enchant_speed_attack_001: {
    id: 'enchant_speed_attack_001',
    name: 'Encantamiento de Velocidad de Ataque Menor',
    description: '+0.1 Velocidad de Ataque',
    modifiers: { attackSpeed: 0.1 },
  },
  enchant_speed_attack_002: {
    id: 'enchant_speed_attack_002',
    name: 'Encantamiento de Velocidad de Ataque',
    description: '+0.2 Velocidad de Ataque',
    modifiers: { attackSpeed: 0.2 },
  },
  enchant_speed_attack_003: {
    id: 'enchant_speed_attack_003',
    name: 'Encantamiento de Velocidad de Ataque Mayor',
    description: '+0.3 Velocidad de Ataque',
    modifiers: { attackSpeed: 0.3 },
  },
  enchant_speed_attack_004: {
    id: 'enchant_speed_attack_004',
    name: 'Encantamiento Maestro de Velocidad de Ataque',
    description: '+0.5 Velocidad de Ataque',
    modifiers: { attackSpeed: 0.5 },
  },

  // Encantamientos de Crítico
  enchant_crit_chance_001: {
    id: 'enchant_crit_chance_001',
    name: 'Encantamiento de Probabilidad de Crítico Menor',
    description: '+5% Probabilidad de Crítico',
    modifiers: { critChance: 0.05 },
  },
  enchant_crit_chance_002: {
    id: 'enchant_crit_chance_002',
    name: 'Encantamiento de Probabilidad de Crítico',
    description: '+10% Probabilidad de Crítico',
    modifiers: { critChance: 0.1 },
  },
  enchant_crit_chance_003: {
    id: 'enchant_crit_chance_003',
    name: 'Encantamiento de Probabilidad de Crítico Mayor',
    description: '+15% Probabilidad de Crítico',
    modifiers: { critChance: 0.15 },
  },
  enchant_crit_chance_004: {
    id: 'enchant_crit_chance_004',
    name: 'Encantamiento Maestro de Probabilidad de Crítico',
    description: '+25% Probabilidad de Crítico',
    modifiers: { critChance: 0.25 },
  },
  enchant_crit_mult_001: {
    id: 'enchant_crit_mult_001',
    name: 'Encantamiento de Multiplicador de Crítico Menor',
    description: '+0.2 Multiplicador de Crítico',
    modifiers: { critMultiplier: 0.2 },
  },
  enchant_crit_mult_002: {
    id: 'enchant_crit_mult_002',
    name: 'Encantamiento de Multiplicador de Crítico',
    description: '+0.5 Multiplicador de Crítico',
    modifiers: { critMultiplier: 0.5 },
  },
  enchant_crit_mult_003: {
    id: 'enchant_crit_mult_003',
    name: 'Encantamiento de Multiplicador de Crítico Mayor',
    description: '+0.8 Multiplicador de Crítico',
    modifiers: { critMultiplier: 0.8 },
  },
  enchant_crit_mult_004: {
    id: 'enchant_crit_mult_004',
    name: 'Encantamiento Maestro de Multiplicador de Crítico',
    description: '+1.2 Multiplicador de Crítico',
    modifiers: { critMultiplier: 1.2 },
  },

  // Encantamientos de Evasión
  enchant_evasion_001: {
    id: 'enchant_evasion_001',
    name: 'Encantamiento de Evasión Menor',
    description: '+3% Evasión',
    modifiers: { evasion: 0.03 },
  },
  enchant_evasion_002: {
    id: 'enchant_evasion_002',
    name: 'Encantamiento de Evasión',
    description: '+5% Evasión',
    modifiers: { evasion: 0.05 },
  },
  enchant_evasion_003: {
    id: 'enchant_evasion_003',
    name: 'Encantamiento de Evasión Mayor',
    description: '+8% Evasión',
    modifiers: { evasion: 0.08 },
  },
  enchant_evasion_004: {
    id: 'enchant_evasion_004',
    name: 'Encantamiento Maestro de Evasión',
    description: '+15% Evasión',
    modifiers: { evasion: 0.15 },
  },

  // Encantamientos de Regeneración
  enchant_regen_hp_001: {
    id: 'enchant_regen_hp_001',
    name: 'Encantamiento de Regeneración de Vida Menor',
    description: '+0.2 Regen HP',
    modifiers: { regenHP: 0.2 },
  },
  enchant_regen_hp_002: {
    id: 'enchant_regen_hp_002',
    name: 'Encantamiento de Regeneración de Vida',
    description: '+0.5 Regen HP',
    modifiers: { regenHP: 0.5 },
  },
  enchant_regen_hp_003: {
    id: 'enchant_regen_hp_003',
    name: 'Encantamiento de Regeneración de Vida Mayor',
    description: '+0.8 Regen HP',
    modifiers: { regenHP: 0.8 },
  },
  enchant_regen_hp_004: {
    id: 'enchant_regen_hp_004',
    name: 'Encantamiento Maestro de Regeneración de Vida',
    description: '+1.5 Regen HP',
    modifiers: { regenHP: 1.5 },
  },
  enchant_regen_mana_001: {
    id: 'enchant_regen_mana_001',
    name: 'Encantamiento de Regeneración de Mana Menor',
    description: '+0.2 Regen Mana',
    modifiers: { regenMana: 0.2 },
  },
  enchant_regen_mana_002: {
    id: 'enchant_regen_mana_002',
    name: 'Encantamiento de Regeneración de Mana',
    description: '+0.5 Regen Mana',
    modifiers: { regenMana: 0.5 },
  },
  enchant_regen_mana_003: {
    id: 'enchant_regen_mana_003',
    name: 'Encantamiento de Regeneración de Mana Mayor',
    description: '+0.8 Regen Mana',
    modifiers: { regenMana: 0.8 },
  },
  enchant_regen_mana_004: {
    id: 'enchant_regen_mana_004',
    name: 'Encantamiento Maestro de Regeneración de Mana',
    description: '+1.5 Regen Mana',
    modifiers: { regenMana: 1.5 },
  },

  // Encantamientos de Stamina
  enchant_stamina_001: {
    id: 'enchant_stamina_001',
    name: 'Encantamiento de Stamina Menor',
    description: '+20 Stamina máximo',
    modifiers: { stamina: 20 },
  },
  enchant_stamina_002: {
    id: 'enchant_stamina_002',
    name: 'Encantamiento de Stamina',
    description: '+50 Stamina máximo',
    modifiers: { stamina: 50 },
  },
  enchant_stamina_003: {
    id: 'enchant_stamina_003',
    name: 'Encantamiento de Stamina Mayor',
    description: '+100 Stamina máximo',
    modifiers: { stamina: 100 },
  },
  enchant_stamina_004: {
    id: 'enchant_stamina_004',
    name: 'Encantamiento Maestro de Stamina',
    description: '+200 Stamina máximo',
    modifiers: { stamina: 200 },
  },
  enchant_regen_stamina_001: {
    id: 'enchant_regen_stamina_001',
    name: 'Encantamiento de Regeneración de Stamina Menor',
    description: '+0.2 Regen Stamina',
    modifiers: { regenStamina: 0.2 },
  },
  enchant_regen_stamina_002: {
    id: 'enchant_regen_stamina_002',
    name: 'Encantamiento de Regeneración de Stamina',
    description: '+0.5 Regen Stamina',
    modifiers: { regenStamina: 0.5 },
  },
  enchant_regen_stamina_003: {
    id: 'enchant_regen_stamina_003',
    name: 'Encantamiento de Regeneración de Stamina Mayor',
    description: '+0.8 Regen Stamina',
    modifiers: { regenStamina: 0.8 },
  },
  enchant_regen_stamina_004: {
    id: 'enchant_regen_stamina_004',
    name: 'Encantamiento Maestro de Regeneración de Stamina',
    description: '+1.5 Regen Stamina',
    modifiers: { regenStamina: 1.5 },
  },

  // Encantamientos Porcentuales de Vida (HP)
  enchant_hp_pct_001: {
    id: 'enchant_hp_pct_001',
    name: 'Encantamiento de Vida Porcentual Menor',
    description: '+10% HP máximo',
    modifiers: { hpPercent: 0.1 },
  },
  enchant_hp_pct_002: {
    id: 'enchant_hp_pct_002',
    name: 'Encantamiento de Vida Porcentual',
    description: '+20% HP máximo',
    modifiers: { hpPercent: 0.2 },
  },
  enchant_hp_pct_003: {
    id: 'enchant_hp_pct_003',
    name: 'Encantamiento de Vida Porcentual Mayor',
    description: '+30% HP máximo',
    modifiers: { hpPercent: 0.3 },
  },
  enchant_hp_pct_004: {
    id: 'enchant_hp_pct_004',
    name: 'Encantamiento Maestro de Vida Porcentual',
    description: '+50% HP máximo',
    modifiers: { hpPercent: 0.5 },
  },

  // Encantamientos Porcentuales de Mana
  enchant_mana_pct_001: {
    id: 'enchant_mana_pct_001',
    name: 'Encantamiento de Mana Porcentual Menor',
    description: '+10% Mana máximo',
    modifiers: { manaPercent: 0.1 },
  },
  enchant_mana_pct_002: {
    id: 'enchant_mana_pct_002',
    name: 'Encantamiento de Mana Porcentual',
    description: '+20% Mana máximo',
    modifiers: { manaPercent: 0.2 },
  },
  enchant_mana_pct_003: {
    id: 'enchant_mana_pct_003',
    name: 'Encantamiento de Mana Porcentual Mayor',
    description: '+30% Mana máximo',
    modifiers: { manaPercent: 0.3 },
  },
  enchant_mana_pct_004: {
    id: 'enchant_mana_pct_004',
    name: 'Encantamiento Maestro de Mana Porcentual',
    description: '+50% Mana máximo',
    modifiers: { manaPercent: 0.5 },
  },

  // Encantamientos Porcentuales de Stamina
  enchant_stamina_pct_001: {
    id: 'enchant_stamina_pct_001',
    name: 'Encantamiento de Stamina Porcentual Menor',
    description: '+10% Stamina máximo',
    modifiers: { staminaPercent: 0.1 },
  },
  enchant_stamina_pct_002: {
    id: 'enchant_stamina_pct_002',
    name: 'Encantamiento de Stamina Porcentual',
    description: '+20% Stamina máximo',
    modifiers: { staminaPercent: 0.2 },
  },
  enchant_stamina_pct_003: {
    id: 'enchant_stamina_pct_003',
    name: 'Encantamiento de Stamina Porcentual Mayor',
    description: '+30% Stamina máximo',
    modifiers: { staminaPercent: 0.3 },
  },
  enchant_stamina_pct_004: {
    id: 'enchant_stamina_pct_004',
    name: 'Encantamiento Maestro de Stamina Porcentual',
    description: '+50% Stamina máximo',
    modifiers: { staminaPercent: 0.5 },
  },

  // Encantamientos Porcentuales de Daño
  enchant_damage_pct_001: {
    id: 'enchant_damage_pct_001',
    name: 'Encantamiento de Daño Porcentual Menor',
    description: '+8% Daño',
    modifiers: { damagePercent: 0.08 },
  },
  enchant_damage_pct_002: {
    id: 'enchant_damage_pct_002',
    name: 'Encantamiento de Daño Porcentual',
    description: '+15% Daño',
    modifiers: { damagePercent: 0.15 },
  },
  enchant_damage_pct_003: {
    id: 'enchant_damage_pct_003',
    name: 'Encantamiento de Daño Porcentual Mayor',
    description: '+25% Daño',
    modifiers: { damagePercent: 0.25 },
  },
  enchant_damage_pct_004: {
    id: 'enchant_damage_pct_004',
    name: 'Encantamiento Maestro de Daño Porcentual',
    description: '+40% Daño',
    modifiers: { damagePercent: 0.4 },
  },

  // Encantamientos Porcentuales de Velocidad de Ataque
  enchant_speed_attack_pct_001: {
    id: 'enchant_speed_attack_pct_001',
    name: 'Encantamiento de Velocidad de Ataque Porcentual Menor',
    description: '+5% Velocidad de Ataque',
    modifiers: { attackSpeedPercent: 0.05 },
  },
  enchant_speed_attack_pct_002: {
    id: 'enchant_speed_attack_pct_002',
    name: 'Encantamiento de Velocidad de Ataque Porcentual',
    description: '+10% Velocidad de Ataque',
    modifiers: { attackSpeedPercent: 0.1 },
  },
  enchant_speed_attack_pct_003: {
    id: 'enchant_speed_attack_pct_003',
    name: 'Encantamiento de Velocidad de Ataque Porcentual Mayor',
    description: '+15% Velocidad de Ataque',
    modifiers: { attackSpeedPercent: 0.15 },
  },
  enchant_speed_attack_pct_004: {
    id: 'enchant_speed_attack_pct_004',
    name: 'Encantamiento Maestro de Velocidad de Ataque Porcentual',
    description: '+25% Velocidad de Ataque',
    modifiers: { attackSpeedPercent: 0.25 },
  },

  // Encantamientos Porcentuales de Velocidad de Movimiento
  enchant_speed_move_pct_001: {
    id: 'enchant_speed_move_pct_001',
    name: 'Encantamiento de Velocidad de Movimiento Porcentual Menor',
    description: '+5% Velocidad de Movimiento',
    modifiers: { moveSpeedPercent: 0.05 },
  },
  enchant_speed_move_pct_002: {
    id: 'enchant_speed_move_pct_002',
    name: 'Encantamiento de Velocidad de Movimiento Porcentual',
    description: '+10% Velocidad de Movimiento',
    modifiers: { moveSpeedPercent: 0.1 },
  },
  enchant_speed_move_pct_003: {
    id: 'enchant_speed_move_pct_003',
    name: 'Encantamiento de Velocidad de Movimiento Porcentual Mayor',
    description: '+15% Velocidad de Movimiento',
    modifiers: { moveSpeedPercent: 0.15 },
  },
  enchant_speed_move_pct_004: {
    id: 'enchant_speed_move_pct_004',
    name: 'Encantamiento Maestro de Velocidad de Movimiento Porcentual',
    description: '+25% Velocidad de Movimiento',
    modifiers: { moveSpeedPercent: 0.25 },
  },

  // Encantamientos Porcentuales de Chance de Crítico (aditivos, no multiplicativos)
  enchant_crit_chance_pct_001: {
    id: 'enchant_crit_chance_pct_001',
    name: 'Encantamiento de Probabilidad de Crítico Porcentual Menor',
    description: '+3% Probabilidad de Crítico',
    modifiers: { critChancePercent: 0.03 },
  },
  enchant_crit_chance_pct_002: {
    id: 'enchant_crit_chance_pct_002',
    name: 'Encantamiento de Probabilidad de Crítico Porcentual',
    description: '+5% Probabilidad de Crítico',
    modifiers: { critChancePercent: 0.05 },
  },
  enchant_crit_chance_pct_003: {
    id: 'enchant_crit_chance_pct_003',
    name: 'Encantamiento de Probabilidad de Crítico Porcentual Mayor',
    description: '+8% Probabilidad de Crítico',
    modifiers: { critChancePercent: 0.08 },
  },
  enchant_crit_chance_pct_004: {
    id: 'enchant_crit_chance_pct_004',
    name: 'Encantamiento Maestro de Probabilidad de Crítico Porcentual',
    description: '+15% Probabilidad de Crítico',
    modifiers: { critChancePercent: 0.15 },
  },

  // Encantamientos de Robo de Vida (Life Steal)
  enchant_lifesteal_phys_001: {
    id: 'enchant_lifesteal_phys_001',
    name: 'Encantamiento de Robo de Vida Físico Menor',
    description: '+3% Robo de Vida por Daño Físico',
    modifiers: { lifeStealPhysical: 0.03 },
  },
  enchant_lifesteal_phys_002: {
    id: 'enchant_lifesteal_phys_002',
    name: 'Encantamiento de Robo de Vida Físico',
    description: '+5% Robo de Vida por Daño Físico',
    modifiers: { lifeStealPhysical: 0.05 },
  },
  enchant_lifesteal_phys_003: {
    id: 'enchant_lifesteal_phys_003',
    name: 'Encantamiento de Robo de Vida Físico Mayor',
    description: '+8% Robo de Vida por Daño Físico',
    modifiers: { lifeStealPhysical: 0.08 },
  },
  enchant_lifesteal_phys_004: {
    id: 'enchant_lifesteal_phys_004',
    name: 'Encantamiento Maestro de Robo de Vida Físico',
    description: '+15% Robo de Vida por Daño Físico',
    modifiers: { lifeStealPhysical: 0.15 },
  },
  enchant_lifesteal_magic_001: {
    id: 'enchant_lifesteal_magic_001',
    name: 'Encantamiento de Robo de Vida Mágico Menor',
    description: '+3% Robo de Vida por Daño Mágico',
    modifiers: { lifeStealMagic: 0.03 },
  },
  enchant_lifesteal_magic_002: {
    id: 'enchant_lifesteal_magic_002',
    name: 'Encantamiento de Robo de Vida Mágico',
    description: '+5% Robo de Vida por Daño Mágico',
    modifiers: { lifeStealMagic: 0.05 },
  },
  enchant_lifesteal_magic_003: {
    id: 'enchant_lifesteal_magic_003',
    name: 'Encantamiento de Robo de Vida Mágico Mayor',
    description: '+8% Robo de Vida por Daño Mágico',
    modifiers: { lifeStealMagic: 0.08 },
  },
  enchant_lifesteal_magic_004: {
    id: 'enchant_lifesteal_magic_004',
    name: 'Encantamiento Maestro de Robo de Vida Mágico',
    description: '+15% Robo de Vida por Daño Mágico',
    modifiers: { lifeStealMagic: 0.15 },
  },

  // Encantamientos de Reflejar Daño
  enchant_reflect_phys_001: {
    id: 'enchant_reflect_phys_001',
    name: 'Encantamiento de Reflejo Físico Menor',
    description: '+5% Reflejar Daño Físico Recibido',
    modifiers: { reflectPhysicalDamage: 0.05 },
  },
  enchant_reflect_phys_002: {
    id: 'enchant_reflect_phys_002',
    name: 'Encantamiento de Reflejo Físico',
    description: '+10% Reflejar Daño Físico Recibido',
    modifiers: { reflectPhysicalDamage: 0.1 },
  },
  enchant_reflect_phys_003: {
    id: 'enchant_reflect_phys_003',
    name: 'Encantamiento de Reflejo Físico Mayor',
    description: '+15% Reflejar Daño Físico Recibido',
    modifiers: { reflectPhysicalDamage: 0.15 },
  },
  enchant_reflect_phys_004: {
    id: 'enchant_reflect_phys_004',
    name: 'Encantamiento Maestro de Reflejo Físico',
    description: '+25% Reflejar Daño Físico Recibido',
    modifiers: { reflectPhysicalDamage: 0.25 },
  },
  enchant_reflect_magic_001: {
    id: 'enchant_reflect_magic_001',
    name: 'Encantamiento de Reflejo Mágico Menor',
    description: '+5% Reflejar Daño Mágico Recibido',
    modifiers: { reflectMagicDamage: 0.05 },
  },
  enchant_reflect_magic_002: {
    id: 'enchant_reflect_magic_002',
    name: 'Encantamiento de Reflejo Mágico',
    description: '+10% Reflejar Daño Mágico Recibido',
    modifiers: { reflectMagicDamage: 0.1 },
  },
  enchant_reflect_magic_003: {
    id: 'enchant_reflect_magic_003',
    name: 'Encantamiento de Reflejo Mágico Mayor',
    description: '+15% Reflejar Daño Mágico Recibido',
    modifiers: { reflectMagicDamage: 0.15 },
  },
  enchant_reflect_magic_004: {
    id: 'enchant_reflect_magic_004',
    name: 'Encantamiento Maestro de Reflejo Mágico',
    description: '+25% Reflejar Daño Mágico Recibido',
    modifiers: { reflectMagicDamage: 0.25 },
  },

  // Encantamientos de Curación
  enchant_healing_output_001: {
    id: 'enchant_healing_output_001',
    name: 'Encantamiento de Curación Mejorada Menor',
    description: '+10% Curación a Terceros',
    modifiers: { healingOutput: 0.1 },
  },
  enchant_healing_output_002: {
    id: 'enchant_healing_output_002',
    name: 'Encantamiento de Curación Mejorada',
    description: '+20% Curación a Terceros',
    modifiers: { healingOutput: 0.2 },
  },
  enchant_healing_output_003: {
    id: 'enchant_healing_output_003',
    name: 'Encantamiento de Curación Mejorada Mayor',
    description: '+30% Curación a Terceros',
    modifiers: { healingOutput: 0.3 },
  },
  enchant_healing_output_004: {
    id: 'enchant_healing_output_004',
    name: 'Encantamiento Maestro de Curación Mejorada',
    description: '+50% Curación a Terceros',
    modifiers: { healingOutput: 0.5 },
  },
  enchant_healing_received_001: {
    id: 'enchant_healing_received_001',
    name: 'Encantamiento de Receptividad a Curación Menor',
    description: '+10% Curación Recibida',
    modifiers: { healingReceived: 0.1 },
  },
  enchant_healing_received_002: {
    id: 'enchant_healing_received_002',
    name: 'Encantamiento de Receptividad a Curación',
    description: '+20% Curación Recibida',
    modifiers: { healingReceived: 0.2 },
  },
  enchant_healing_received_003: {
    id: 'enchant_healing_received_003',
    name: 'Encantamiento de Receptividad a Curación Mayor',
    description: '+30% Curación Recibida',
    modifiers: { healingReceived: 0.3 },
  },
  enchant_healing_received_004: {
    id: 'enchant_healing_received_004',
    name: 'Encantamiento Maestro de Receptividad a Curación',
    description: '+50% Curación Recibida',
    modifiers: { healingReceived: 0.5 },
  },

  // Encantamientos de Flags Especiales
  enchant_silent_walk_001: {
    id: 'enchant_silent_walk_001',
    name: 'Encantamiento de Paso Sigiloso',
    description: 'No emitir ruido al caminar',
    modifiers: { noWalkNoise: 1 },
  },
  enchant_silent_cast_001: {
    id: 'enchant_silent_cast_001',
    name: 'Encantamiento de Hechizo Silencioso',
    description: 'No emitir palabras mágicas al castear',
    modifiers: { noSpellWords: 1 },
  },
  enchant_no_stamina_phys_001: {
    id: 'enchant_no_stamina_phys_001',
    name: 'Encantamiento de Ataque Incesante',
    description: 'No consumir energía al atacar físicamente',
    modifiers: { noStaminaCostPhysical: 1 },
  },

  // Encantamientos para Invocaciones
  enchant_summon_hp_001: {
    id: 'enchant_summon_hp_001',
    name: 'Encantamiento de Invocación Resistente Menor',
    description: '+30 HP para Invocaciones',
    modifiers: { summonHpBonus: 30 },
  },
  enchant_summon_hp_002: {
    id: 'enchant_summon_hp_002',
    name: 'Encantamiento de Invocación Resistente',
    description: '+60 HP para Invocaciones',
    modifiers: { summonHpBonus: 60 },
  },
  enchant_summon_hp_003: {
    id: 'enchant_summon_hp_003',
    name: 'Encantamiento de Invocación Resistente Mayor',
    description: '+120 HP para Invocaciones',
    modifiers: { summonHpBonus: 120 },
  },
  enchant_summon_hp_004: {
    id: 'enchant_summon_hp_004',
    name: 'Encantamiento Maestro de Invocación Resistente',
    description: '+250 HP para Invocaciones',
    modifiers: { summonHpBonus: 250 },
  },
  enchant_summon_atkspeed_001: {
    id: 'enchant_summon_atkspeed_001',
    name: 'Encantamiento de Invocación Veloz Menor',
    description: '+0.1 Velocidad de Ataque para Invocaciones',
    modifiers: { summonAttackSpeedBonus: 0.1 },
  },
  enchant_summon_atkspeed_002: {
    id: 'enchant_summon_atkspeed_002',
    name: 'Encantamiento de Invocación Veloz',
    description: '+0.2 Velocidad de Ataque para Invocaciones',
    modifiers: { summonAttackSpeedBonus: 0.2 },
  },
  enchant_summon_atkspeed_003: {
    id: 'enchant_summon_atkspeed_003',
    name: 'Encantamiento de Invocación Veloz Mayor',
    description: '+0.3 Velocidad de Ataque para Invocaciones',
    modifiers: { summonAttackSpeedBonus: 0.3 },
  },
  enchant_summon_atkspeed_004: {
    id: 'enchant_summon_atkspeed_004',
    name: 'Encantamiento Maestro de Invocación Veloz',
    description: '+0.5 Velocidad de Ataque para Invocaciones',
    modifiers: { summonAttackSpeedBonus: 0.5 },
  },
  enchant_summon_physdmg_001: {
    id: 'enchant_summon_physdmg_001',
    name: 'Encantamiento de Invocación Fuerte Menor',
    description: '+5 Daño Físico para Invocaciones',
    modifiers: { summonPhysicalDamageBonus: 5 },
  },
  enchant_summon_physdmg_002: {
    id: 'enchant_summon_physdmg_002',
    name: 'Encantamiento de Invocación Fuerte',
    description: '+10 Daño Físico para Invocaciones',
    modifiers: { summonPhysicalDamageBonus: 10 },
  },
  enchant_summon_physdmg_003: {
    id: 'enchant_summon_physdmg_003',
    name: 'Encantamiento de Invocación Fuerte Mayor',
    description: '+20 Daño Físico para Invocaciones',
    modifiers: { summonPhysicalDamageBonus: 20 },
  },
  enchant_summon_physdmg_004: {
    id: 'enchant_summon_physdmg_004',
    name: 'Encantamiento Maestro de Invocación Fuerte',
    description: '+40 Daño Físico para Invocaciones',
    modifiers: { summonPhysicalDamageBonus: 40 },
  },
  enchant_summon_magicdmg_001: {
    id: 'enchant_summon_magicdmg_001',
    name: 'Encantamiento de Invocación Mágica Menor',
    description: '+5 Daño Mágico para Invocaciones',
    modifiers: { summonMagicDamageBonus: 5 },
  },
  enchant_summon_magicdmg_002: {
    id: 'enchant_summon_magicdmg_002',
    name: 'Encantamiento de Invocación Mágica',
    description: '+10 Daño Mágico para Invocaciones',
    modifiers: { summonMagicDamageBonus: 10 },
  },
  enchant_summon_magicdmg_003: {
    id: 'enchant_summon_magicdmg_003',
    name: 'Encantamiento de Invocación Mágica Mayor',
    description: '+20 Daño Mágico para Invocaciones',
    modifiers: { summonMagicDamageBonus: 20 },
  },
  enchant_summon_magicdmg_004: {
    id: 'enchant_summon_magicdmg_004',
    name: 'Encantamiento Maestro de Invocación Mágica',
    description: '+40 Daño Mágico para Invocaciones',
    modifiers: { summonMagicDamageBonus: 40 },
  },
};

/**
 * Obtiene la definición de un encantamiento por su ID
 */
export function getEnchantmentDefinition(enchantId: string | null | undefined): EnchantmentDefinition | null {
  if (!enchantId) return null;
  return ENCHANTMENTS[enchantId] ?? null;
}

/**
 * Obtiene todos los encantamientos disponibles
 */
export function getAllEnchantments(): EnchantmentDefinition[] {
  return Object.values(ENCHANTMENTS);
}

/**
 * Obtiene los modificadores de un encantamiento
 */
export function getEnchantmentModifiers(enchantId: string | null | undefined): ItemModifiers {
  const enchant = getEnchantmentDefinition(enchantId);
  return enchant?.modifiers ?? {};
}
